import React from "react"
import { useTranslation } from "react-i18next"
import { Platform, View } from "react-native"
import { useQuery } from "react-query"

import {
  SubscriptionPlanResponse,
  UserSubscription,
  UserSubscriptionsResponse,
} from "@treefort/api-spec"
import { useAuth } from "@treefort/lib/auth-provider"

import { ActionLink, AsyncActionLink } from "../../../components/action-link"
import Box from "../../../components/box"
import Column from "../../../components/column"
import { Heading } from "../../../components/heading"
import { RestorePurchasesLink } from "../../../components/restore-purchases-link"
import { SubscriptionCard } from "../../../components/subscription-card"
import Text from "../../../components/text"
import config from "../../../config"
import {
  refetchUserSubscriptions,
  useUserSubscriptions,
} from "../../../hooks/subscriptions"
import { useSubscriptionPlan } from "../../../hooks/subscriptions"
import { useAsyncViewPropsForQueries } from "../../../hooks/use-async-view-props-for-queries"
import { useCanCancelSubscription } from "../../../hooks/use-can-cancel-subscription"
import { useOpenCheckoutPage } from "../../../hooks/use-open-checkout-page"
import { useSignUpOptionsData } from "../../../hooks/use-sign-up-options-data"
import useUserInfo from "../../../hooks/use-user-info"
import { kebabCase } from "../../../lib/kebab-case"
import {
  canOpenBillingPortalForProvider,
  canPurchasePlanFromProvider,
  openBillingPortal,
} from "../../../lib/subscription-plans"
import {
  getCurrentSubscription,
  subscriptionWillRenew,
} from "../../../lib/subscriptions"
import MenuLayout from "../../layouts/menu"

export function SubscriptionScreen(): React.JSX.Element {
  const auth = useAuth()
  const userSubscriptions = useUserSubscriptions()
  const subscription = getCurrentSubscription(userSubscriptions.data)
  const signUpOptionsData = useSignUpOptionsData()

  const pageState =
    !auth.loading && auth.user === null
      ? "UNAUTHENTICATED"
      : userSubscriptions.data?.redundantSubscriptionIds.length
        ? "DOUBLE_SUBSCRIBED"
        : subscription
          ? "MANAGE_SUBSCRIPTION"
          : "NO_SUBSCRIPTION"

  const asyncViewProps = useAsyncViewPropsForQueries(
    [userSubscriptions, signUpOptionsData],
    { forceLoading: auth.loading || pageState === "UNAUTHENTICATED" },
  )

  const subscriptionCard = userSubscriptions.data ? (
    <SubscriptionCard userSubscriptions={userSubscriptions.data} />
  ) : null

  return (
    <MenuLayout asyncViewProps={asyncViewProps}>
      <Column gap="large" alignItems="stretch">
        {pageState === "DOUBLE_SUBSCRIBED" ? (
          <DoubleSubscriptionMessage />
        ) : pageState === "MANAGE_SUBSCRIPTION" && userSubscriptions.data ? (
          <>
            {subscriptionCard}
            <ManageSubscription userSubscriptions={userSubscriptions.data} />
          </>
        ) : (
          <NoSubscriptionMessage />
        )}
      </Column>
    </MenuLayout>
  )
}

/**
 * Message to show users with no subscription. There's no way to end up on this
 * page without a subscription except through a deep link, so there's no reason
 * to get fancy with what we display.
 */
function NoSubscriptionMessage() {
  const { t } = useTranslation()
  return (
    <Heading textStyle="headingMedium" alignment="center" level={3}>
      {t("No active subscription found.")}
    </Heading>
  )
}

/**
 * Message to show users who have more than one subscription. Really we should
 * update this with details about each subscription.
 */
function DoubleSubscriptionMessage(): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <>
      <Heading textStyle="headingMedium" alignment="center" level={3}>
        {t("You have multiple subscriptions.")}
      </Heading>
      <Text color="secondary" alignment="center" textStyle="body">
        {t(
          "There are multiple active subscriptions associated with your account.",
        )}{" "}
        {t("Please contact us for help resolving this.")}
      </Text>
      <ActionLink
        id="contact-us-multiple-subs"
        to={{ type: "path", path: "/menu/contact" }}
      >
        {t("Contact us")}
      </ActionLink>
    </>
  )
}

export function ManageSubscription({
  userSubscriptions,
}: {
  userSubscriptions: UserSubscriptionsResponse
}): React.JSX.Element {
  const { t } = useTranslation()
  const subscription = getCurrentSubscription(userSubscriptions)
  const { data: subscriptionPlan } = useSubscriptionPlan(
    subscription?.subscriptionPlanId,
  )
  const { data: canOpenBillingPortal } = useQuery(
    ["canOpenBillingPortal", subscriptionPlan?.provider, subscription],
    () =>
      canOpenBillingPortalForProvider(subscriptionPlan?.provider, subscription),
  )
  const manageElsewhereMessage = getManageElsewhereMessage({
    subscription,
    t,
    canOpenBillingPortal,
  })
  return (
    <View>
      {manageElsewhereMessage ? (
        <Box paddingBottom="small" paddingHorizontal="medium">
          <Text color="secondary" textStyle="body">
            {manageElsewhereMessage}
          </Text>
        </Box>
      ) : null}
      <BillingPortalLink
        subscription={subscription}
        subscriptionPlan={subscriptionPlan}
        canOpenBillingPortal={canOpenBillingPortal}
      />
      <ChangePlansLink subscription={subscription} />
      <CancelLink subscription={subscription} />
      <ActionLink
        id="contact-us-menu-link"
        to={{ type: "path", path: "/menu/contact" }}
      >
        {t("Contact us")}
      </ActionLink>
      <RestorePurchasesLink paddingTop="small" />
    </View>
  )
}

/**
 * Render a button that opens the billing portal for the user's plan's provider.
 */
function BillingPortalLink({
  subscription,
  subscriptionPlan,
  canOpenBillingPortal,
}: {
  subscription: UserSubscription | undefined
  subscriptionPlan: SubscriptionPlanResponse | undefined
  canOpenBillingPortal: boolean | undefined
}) {
  const { t, i18n } = useTranslation()
  return canOpenBillingPortal && subscriptionPlan ? (
    <AsyncActionLink
      id={`open-billing-portal-${subscriptionPlan.provider}`}
      onPress={async () => {
        await openBillingPortal({ subscription, subscriptionPlan, i18n })

        // Refetch user subscriptions in case the user changed anything whilst
        // in the billing portal. This will not run on the web because
        // openBillingPortal redirects the user to another page, unloading our
        // web app. This does not need to run on the web because subscriptions
        // will be fetched anew when the user is redirected back to our web app.
        await refetchUserSubscriptions()
      }}
    >
      {t("Manage billing")}
    </AsyncActionLink>
  ) : null
}

/**
 * Render a button that takes the user to the checkout page to pick a new plan.
 */
function ChangePlansLink({
  subscription,
}: {
  subscription: UserSubscription | undefined
}) {
  const userInfo = useUserInfo()
  const signUpOptionsData = useSignUpOptionsData()
  const openCheckoutPage = useOpenCheckoutPage()

  const { data: canPurchasePlan } = useQuery(
    ["canPurchasePlan", subscription?.provider],
    () => canPurchasePlanFromProvider(subscription?.provider),
  )

  const isSubscribed =
    typeof userInfo.data?.subscription.subscribed === "number"
  const isCanceledWithTimeLeft =
    isSubscribed && userInfo.data?.subscription.canceledAt

  const { t } = useTranslation()

  const linkText =
    !isSubscribed || isCanceledWithTimeLeft
      ? t("View plans")
      : t("Change plans")

  return userInfo.data &&
    signUpOptionsData.data?.availableSubscriptionPlans.length &&
    // Show the link if the user doesn't have a renewing subscription _or_ if
    // the user's subscription was purchased on the current platform (in which
    // case we can rely on the platform to handle things like proration).
    (!subscriptionWillRenew(subscription) || canPurchasePlan) ? (
    <ActionLink
      id={kebabCase(
        `${linkText}-${subscription?.provider || "no-sub-provider"}`,
      )}
      onPress={openCheckoutPage}
    >
      {linkText}
    </ActionLink>
  ) : null
}

/**
 * Render a cancel button if we've got an a renewing plan and the plan's
 * provider supports cancellation
 */
function CancelLink({
  subscription,
}: {
  subscription: UserSubscription | undefined
}) {
  const { t } = useTranslation()

  const { data: canCancel } = useCanCancelSubscription(subscription)
  return subscription && canCancel ? (
    <ActionLink
      id={kebabCase(`cancel-sub-${subscription.provider}`)}
      to={{ type: "path", path: "/menu/subscription/cancel" }}
    >
      {t("Cancel subscription")}
    </ActionLink>
  ) : null
}

/**
 * Message to show users who subscribed on a different platform and have to go
 * elsewhere to manage their subscription.
 */
function getManageElsewhereMessage({
  subscription,
  t,
  canOpenBillingPortal,
}: {
  subscription: UserSubscription | undefined
  t: ReturnType<typeof useTranslation>["t"]
  canOpenBillingPortal: boolean | undefined
}) {
  switch (subscription?.provider) {
    case "appStore":
      return Platform.OS !== "ios"
        ? t(
            "Your subscription was purchased from Apple and can only be managed via Apple Subscriptions. If you have questions or need help, please contact us.",
          )
        : null
    case "playStore":
      return Platform.OS !== "android"
        ? t(
            "Your subscription was purchased from Google and can only be managed via the Play Store. If you have questions or need help, please contact us.",
          )
        : null
    case "webPayment":
    case "stripe":
      return !canOpenBillingPortal
        ? t(
            "Your subscription was purchased on the web and can only be managed via the {{appName}} web app. If you have questions or need help, please contact us.",
            { appName: config.APP_NAME },
          )
        : null
    default:
      return null
  }
}
